type ActionFn = (...args: unknown[]) => unknown;

type InterceptorParams<T extends Record<string, Record<string, ActionFn>>> = {
  entity: keyof T;
  method: string;
  origAction: ActionFn;
  args: unknown[];
};

type Interceptor<T extends Record<string, Record<string, ActionFn>>> = (params: InterceptorParams<T>) => unknown;

export const interceptActions = <T extends Record<string, Record<string, ActionFn>>>(
  actionsBase: T,
  interceptor: Interceptor<T>
): T => {
  const entityProxyCache = new Map<string, Record<string, ActionFn>>();

  return new Proxy(actionsBase, {
    get(target, entity: string) {
      if (entityProxyCache.has(entity)) {
        return entityProxyCache.get(entity);
      }

      const entityModule = Reflect.get(target, entity) ?? {};

      const methodCache = new Map<string | symbol, ActionFn>();

      const entityProxy = new Proxy(entityModule, {
        get(entityTarget, method) {
          if (methodCache.has(method)) {
            return methodCache.get(method);
          }

          const origAction = Reflect.get(entityTarget, method) as ActionFn;

          const wrapped = (...args: unknown[]) => {
            return interceptor({
              entity: entity as keyof T,
              method: method as string,
              origAction: origAction || (() => {}),
              args,
            });
          };

          methodCache.set(method, wrapped);
          return wrapped;
        },
      });

      entityProxyCache.set(entity, entityProxy);
      return entityProxy;
    },
  }) as T;
};
